import { UseMutationResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useState } from "react";

import { fetchMenuItemList, reOrderMenuItems } from "../../core/menuItem.core";
import { useTranslation } from "../../core/translation.core";
import { MenuItemAdmin } from "../../types/menuItem";
import LocalePs from "../locale/localePs.core";
import { ListSection, Tag } from "../reuseables/list.comp";

export default function MenuItemList() {
  const { t, msg } = useTranslation();

  return (
    <>
      <p className="dark:text-slate-300">{t(msg.menuItem.dragInstruction)}</p>
      <ListSection id="menu-item-list">
        <MenuItemTableBody />
      </ListSection>
    </>
  );
}

interface ReorderParams {
  menuItemId: string;
  newOrder: number;
}

function compareMenuItemOrder(a: MenuItemAdmin, b: MenuItemAdmin): number {
  const ao = Number(a.order);
  const bo = Number(b.order);
  const aOrder = Number.isFinite(ao) ? ao : 0;
  const bOrder = Number.isFinite(bo) ? bo : 0;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return String(a.id).localeCompare(String(b.id));
}

function MenuItemTableBody() {
  const { t, msg } = useTranslation();
  const { data, isFetched } = useQuery({
    queryKey: ["menuItemList"],
    queryFn: fetchMenuItemList,
  });
  const queryClient = useQueryClient();

  const reOrderMutation = useMutation({
    mutationFn: async ({ menuItemId, newOrder }: ReorderParams) => {
      const sortedIds = data!
        .filter((d) => d.id !== menuItemId)
        .sort(compareMenuItemOrder)
        .map((d) => d.id);

      sortedIds.splice(newOrder, 0, menuItemId);

      await reOrderMenuItems(sortedIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["menuItemList"] });
    },
  });

  if (!isFetched || !data) return null;

  const rows = Array.isArray(data) ? data : [];
  const sorted = [...rows].sort(compareMenuItemOrder);

  if (sorted.length === 0) {
    return (
      <div className="px-6 py-8 text-sm text-her-taggray dark:text-slate-400" data-empty="menu-item-list">
        {t(msg.menuItem.empty)}
      </div>
    );
  }

  return (
    <div className="pt-6 lg:pt-0">
      <div className="grid grid-cols-[minmax(3rem,auto)_120px_1fr_1fr_100px] gap-6 px-6 py-3 text-sm font-medium dark:text-slate-200 max-lg:hidden">
        <div>{t(msg.menuItem.order)}</div>
        <div>{t(msg.menuItem.type)}</div>
        <div>{t(msg.general.name)}</div>
        <div>{t(msg.menuItem.target)}</div>
        <div></div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 px-6 py-2 text-xs font-medium text-her-taggray dark:text-slate-400 lg:hidden">
        <div>{t(msg.menuItem.order)}</div>
        <div>{t(msg.menuItem.type)}</div>
      </div>

      {sorted.map((item, idx) => (
        <MenuItemRow key={item.id} item={item} idx={idx} reOrder={reOrderMutation} />
      ))}

      <DropDiv dragIdx={sorted.length} reOrder={reOrderMutation} />
    </div>
  );
}

interface DropDivProps {
  dragIdx: number;
  reOrder: UseMutationResult<void, unknown, ReorderParams, unknown>;
}

const DropDiv = ({ dragIdx, reOrder }: DropDivProps) => {
  const [onDrag, setOnDrag] = useState(false);

  function drop(ev: React.DragEvent<HTMLDivElement>) {
    setOnDrag(false);
    ev.preventDefault();
    if (allowDrag()) {
      const menuItemId = ev.dataTransfer.getData("id");
      reOrder.mutateAsync({ menuItemId, newOrder: dragIdx });
    }

    (window as unknown as { draggingMenuItem?: unknown }).draggingMenuItem = undefined;
  }

  function onDragOver(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
    setOnDrag(allowDrag());
  }

  function allowDrag() {
    const dragging = (window as unknown as { draggingMenuItem?: { item: MenuItemAdmin; srcIdx: number } }).draggingMenuItem;
    if (!dragging) return false;
    const { srcIdx } = dragging;
    if (srcIdx === dragIdx || srcIdx === dragIdx - 1) return false;
    return true;
  }

  return (
    <div onDrop={drop} onDragLeave={() => setOnDrag(false)} onDragOver={onDragOver} className="p-2">
      {onDrag && <div className="h-[50px] border border-dashed rounded-lg bg-gray-400 dark:bg-gray-500" />}
    </div>
  );
};

interface MenuItemRowProps {
  item: MenuItemAdmin;
  idx: number;
  reOrder: UseMutationResult<void, unknown, ReorderParams, unknown>;
}

const MenuItemRow = ({ item, idx, reOrder }: MenuItemRowProps) => {
  const { t, msg } = useTranslation();

  function dragStart(ev: React.DragEvent<HTMLDivElement>) {
    ev.dataTransfer.setData("id", item.id);
    ev.dataTransfer.setData("idx", idx.toString());
    (window as unknown as { draggingMenuItem?: { item: MenuItemAdmin; srcIdx: number } }).draggingMenuItem = {
      item,
      srcIdx: idx,
    };
  }

  return (
    <>
      <DropDiv dragIdx={idx} reOrder={reOrder} />
      <div
        data-menu-item-id={item.id}
        data-menu-item-type={item.type}
        className="grid grid-cols-[minmax(3rem,auto)_auto] lg:grid-cols-[minmax(3rem,auto)_120px_1fr_1fr_100px] gap-x-6 gap-y-3 px-6 py-6 border-t border-slate-200 dark:border-slate-600 text-sm cursor-pointer group/edit dark:text-slate-200"
        draggable
        onDragStart={dragStart}
        data-menu-item-order={item.order}
      >
        <div
          className="row-span-2 lg:row-span-1 tabular-nums font-medium"
          title={`${t(msg.menuItem.order)}: ${item.order}`}
          aria-label={`${t(msg.menuItem.order)} ${item.order}`}
        >
          {item.order}
        </div>
        <div className="col-span-1 lg:col-span-1">
          {item.type === "category" ? t(msg.menuItem.typeCategory) : t(msg.menuItem.typeLink)}
        </div>
        <div>
          {item.type === "category" && item.category?.name && <LocalePs value={item.category.name} />}
          {item.type === "link" && item.label && <LocalePs value={item.label} />}
        </div>
        <div>
          {item.type === "category" && item.category?.slug && (
            <>
              <Tag>{t(msg.category.slug)}</Tag>
              <LocalePs value={item.category.slug} ignoreBadge />
            </>
          )}
          {item.type === "link" && item.href && (
            <>
              <Tag>{t(msg.general.url)}</Tag>
              <LocalePs value={item.href} ignoreBadge />
            </>
          )}
        </div>
        <Link
          href={`/menuItem/edit/${item.id}`}
          className="cursor-pointer opacity-0 group-hover/edit:opacity-100 transition-all lg:justify-self-end"
        >
          {t(msg.form.edit)}
        </Link>
      </div>
    </>
  );
};
